import { z } from 'zod';

import { Id, IsoDateTime } from '../common/primitives';

// ── Enums ──────────────────────────────────────────────────────────────────

export const MobilePlatformSchema = z.enum(['ANDROID', 'IOS', 'WEB_PUSH']);
export type MobilePlatform = z.infer<typeof MobilePlatformSchema>;

export const MobileAppRoleSchema = z.enum(['CUSTOMER', 'VENDOR', 'DELIVERY', 'ADMIN']);
export type MobileAppRole = z.infer<typeof MobileAppRoleSchema>;

export const MobileDeviceStatusSchema = z.enum(['ACTIVE', 'INACTIVE', 'REVOKED']);
export type MobileDeviceStatus = z.infer<typeof MobileDeviceStatusSchema>;

export const AppVersionStatusSchema = z.enum(['DRAFT', 'ACTIVE', 'RETIRED']);
export type AppVersionStatus = z.infer<typeof AppVersionStatusSchema>;

export const VersionGateActionSchema = z.enum(['OK', 'SOFT_UPDATE', 'FORCE_UPDATE']);
export type VersionGateAction = z.infer<typeof VersionGateActionSchema>;

// ── Device ─────────────────────────────────────────────────────────────────

const Semver = z
  .string()
  .trim()
  .regex(/^\d+\.\d+\.\d+(?:[-+][\w.-]+)?$/, 'Invalid semver');

export const MobileDeviceSchema = z.object({
  id: Id,
  userId: Id,
  deviceId: z.string(),
  appRole: MobileAppRoleSchema,
  platform: MobilePlatformSchema,
  pushToken: z.string().nullable(),
  appVersion: z.string().nullable(),
  osVersion: z.string().nullable(),
  model: z.string().nullable(),
  label: z.string().nullable(),
  status: MobileDeviceStatusSchema,
  lastSeenAt: IsoDateTime,
  createdAt: IsoDateTime,
});
export type MobileDevice = z.infer<typeof MobileDeviceSchema>;

export const RegisterDeviceRequest = z
  .object({
    deviceId: z.string().trim().min(4).max(120),
    appRole: MobileAppRoleSchema,
    platform: MobilePlatformSchema,
    pushToken: z.string().trim().max(500).optional(),
    appVersion: Semver.optional(),
    osVersion: z.string().trim().max(40).optional(),
    model: z.string().trim().max(80).optional(),
    label: z.string().trim().max(120).optional(),
  })
  .strict();
export type RegisterDeviceRequest = z.infer<typeof RegisterDeviceRequest>;

export const UpdatePushTokenRequest = z
  .object({
    deviceId: z.string().trim().min(4).max(120),
    pushToken: z.string().trim().min(8).max(500),
  })
  .strict();
export type UpdatePushTokenRequest = z.infer<typeof UpdatePushTokenRequest>;

export const RevokeDeviceRequest = z
  .object({
    deviceId: z.string().trim().min(4).max(120),
  })
  .strict();
export type RevokeDeviceRequest = z.infer<typeof RevokeDeviceRequest>;

// ── App version ────────────────────────────────────────────────────────────

export const AppVersionSchema = z.object({
  id: Id,
  appRole: MobileAppRoleSchema,
  platform: MobilePlatformSchema,
  version: z.string(),
  buildNumber: z.number().int().nonnegative(),
  minSupported: z.string(),
  latestReleased: z.string(),
  storeUrl: z.string().nullable(),
  releaseNotes: z.string().nullable(),
  status: AppVersionStatusSchema,
  releasedAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
});
export type AppVersion = z.infer<typeof AppVersionSchema>;

export const UpsertAppVersionRequest = z
  .object({
    appRole: MobileAppRoleSchema,
    platform: MobilePlatformSchema,
    version: Semver,
    buildNumber: z.number().int().min(0).optional(),
    minSupported: Semver,
    latestReleased: Semver,
    storeUrl: z.string().trim().url().optional(),
    releaseNotes: z.string().trim().max(4000).optional(),
    status: AppVersionStatusSchema.optional(),
    releasedAt: IsoDateTime.optional(),
  })
  .strict();
export type UpsertAppVersionRequest = z.infer<typeof UpsertAppVersionRequest>;

export const VersionGateRequest = z
  .object({
    appRole: MobileAppRoleSchema,
    platform: MobilePlatformSchema,
    currentVersion: Semver,
  })
  .strict();
export type VersionGateRequest = z.infer<typeof VersionGateRequest>;

export const VersionGateResultSchema = z.object({
  action: VersionGateActionSchema,
  minSupported: z.string(),
  latestReleased: z.string(),
  storeUrl: z.string().nullable(),
  releaseNotes: z.string().nullable(),
});
export type VersionGateResult = z.infer<typeof VersionGateResultSchema>;

// ── Push (relay through Notifications module) ──────────────────────────────

export const SendPushRequest = z
  .object({
    /// Target a specific user — broadcasts to all their ACTIVE devices.
    userId: Id.optional(),
    /// Or send to a specific device row.
    deviceId: z.string().trim().min(4).max(120).optional(),
    /// Or broadcast to every ACTIVE device of an app role.
    appRole: MobileAppRoleSchema.optional(),
    title: z.string().trim().min(1).max(120),
    body: z.string().trim().min(1).max(500),
    /// Free-form data payload (deep-link routes, ids).
    data: z.record(z.string()).optional(),
  })
  .strict()
  .refine((v) => v.userId ?? v.deviceId ?? v.appRole, {
    message: 'One of userId / deviceId / appRole is required',
    path: ['userId'],
  });
export type SendPushRequest = z.infer<typeof SendPushRequest>;

export const PushDispatchResultSchema = z.object({
  targeted: z.number().int().nonnegative(),
  delivered: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
});
export type PushDispatchResult = z.infer<typeof PushDispatchResultSchema>;
